import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Fab,
  Breadcrumbs,
  Link,
  Divider,
  LinearProgress,
  InputAdornment,
  TextField,
  useTheme,
  useMediaQuery,
  alpha,
  Stack,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Science as TestTubeIcon,
  Payment,
  Star as StarIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gatewayService, Gateway } from '../services/gatewayService';
import { GatewayForm, GatewayFormData } from '../components/forms';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  filtersPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
} from '../styles/theme';

type GatewayType = 'asaas' | 'iugu' | 'efi' | 'sicoob';

interface GatewaysPageState {
  gateways: Gateway[];
  loading: boolean;
  error: string | null;
  searchTerm: string;
  selectedType: GatewayType | '';
}

interface MenuState {
  anchorEl: HTMLElement | null;
  gateway: Gateway | null;
}

interface DialogState {
  gatewayForm: boolean;
  confirmDelete: boolean;
  selectedGateway: Gateway | null;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const initialState: GatewaysPageState = {
  gateways: [],
  loading: false,
  error: null,
  searchTerm: '',
  selectedType: '',
};

const GatewaysPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [state, setState] = useState<GatewaysPageState>(initialState);
  const [menu, setMenu] = useState<MenuState>({
    anchorEl: null,
    gateway: null,
  });
  const [dialogs, setDialogs] = useState<DialogState>({
    gatewayForm: false,
    confirmDelete: false,
    selectedGateway: null,
  });
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [formLoading, setFormLoading] = useState(false);

  // Carregar gateways
  useEffect(() => {
    loadGateways();
  }, []);

  const loadGateways = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await gatewayService.getAllGateways();
      setState(prev => ({ ...prev, gateways: response.gateways, loading: false }));
    } catch (error: any) {
      console.error('Erro ao carregar gateways:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Erro ao carregar gateways'
      }));
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Menu
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, gateway: Gateway) => {
    setMenu({ anchorEl: event.currentTarget, gateway });
  };

  const handleCloseMenu = () => {
    setMenu({ anchorEl: null, gateway: null });
  };

  // Formulário
  const handleOpenForm = (gateway?: Gateway) => {
    setDialogs(prev => ({
      ...prev,
      gatewayForm: true,
      selectedGateway: gateway || null
    }));
  };

  const handleCloseForm = () => {
    setDialogs(prev => ({
      ...prev,
      gatewayForm: false,
      selectedGateway: null
    }));
  };

  const handleSubmitForm = async (formData: GatewayFormData) => {
    try {
      setFormLoading(true);

      // Transformar os dados para o formato esperado pelo backend
      const { configuracoes, ...baseData } = formData;
      const requestData = {
        ...baseData,
        ...configuracoes, // Achatar as configurações para o nível raiz
      };

      if (dialogs.selectedGateway) {
        await gatewayService.updateGateway(dialogs.selectedGateway.id, requestData);
        showSnackbar('Gateway atualizado com sucesso!');
      } else {
        await gatewayService.createGateway(requestData);
        showSnackbar('Gateway criado com sucesso!');
      }

      handleCloseForm();
      loadGateways();
    } catch (error: any) {
      console.error('Erro ao salvar gateway:', error);
      const apiErrors = error.response?.data?.errors;
      const apiMessage = error.response?.data?.message;

      let errorMsg = apiMessage || error.message || 'Erro ao salvar gateway';
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        errorMsg = apiErrors.map((e: any) => typeof e === 'string' ? e : e.msg || e.message).join(' | ');
      }
      showSnackbar(errorMsg, 'error');
      throw error;
    } finally {
      setFormLoading(false);
    }
  };

  // Exclusão
  const handleDeleteGateway = async () => {
    if (!dialogs.selectedGateway) return;

    try {
      await gatewayService.deleteGateway(dialogs.selectedGateway.id);
      showSnackbar('Gateway excluído com sucesso!');
      setDialogs(prev => ({ ...prev, confirmDelete: false, selectedGateway: null }));
      loadGateways();
    } catch (error: any) {
      console.error('Erro ao excluir gateway:', error);
      showSnackbar(error.message || 'Erro ao excluir gateway', 'error');
    }
  };

  // Toggle status
  const handleToggleStatus = async (gateway: Gateway) => {
    try {
      await gatewayService.toggleGatewayStatus(gateway.id);
      showSnackbar(`Gateway ${gateway.ativo ? 'desativado' : 'ativado'} com sucesso!`);
      loadGateways();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      showSnackbar(error.message || 'Erro ao alterar status', 'error');
    }
  };

  // Testar conectividade
  const handleTestConnection = async (gateway: Gateway) => {
    try {
      const response = await gatewayService.testGatewayConnection(gateway.id);

      if (response.conectado) {
        Swal.fire({
          icon: 'success',
          title: 'Conectado!',
          text: 'Teste de conectividade realizado com sucesso.',
          confirmButtonColor: theme.palette.primary.main,
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Falha na Conectividade',
          text: response.erro_conectividade || 'Ocorreu um erro ao testar a conexão com o gateway.',
          confirmButtonColor: theme.palette.primary.main,
          customClass: {
            container: 'swal2-high-zindex'
          }
        });
      }
      loadGateways();
    } catch (error: any) {
      console.error('Erro ao testar conectividade:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: error.message || 'Erro ao testar conectividade',
        confirmButtonColor: theme.palette.primary.main,
      });
    }
  };

  // Filtros
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, searchTerm: event.target.value }));
  };

  const handleTypeFilter = (type: GatewayType | '') => {
    setState(prev => ({ ...prev, selectedType: type }));
  };

  // Filtrar gateways
  const filteredGateways = state.gateways.filter(gateway => {
    const matchesSearch = gateway.nome.toLowerCase().includes(state.searchTerm.toLowerCase());
    const matchesType = !state.selectedType || gateway.tipo === state.selectedType;
    return matchesSearch && matchesType;
  });

  // Verificar permissões
  const canManageGateways = user?.perfil === 'administrador' || user?.perfil === 'financeiro';

  if (!canManageGateways) {
    return (
      <Box sx={pageShellSx}>
        <Alert severity="warning">Você não tem permissão para acessar esta página.</Alert>
      </Box>
    );
  }

  const totalAtivos = state.gateways.filter(g => g.ativo).length;
  const totalSandbox = state.gateways.filter(g => g.ambiente === 'sandbox').length;
  const totalProducao = state.gateways.filter(g => g.ambiente === 'producao').length;

  return (
    <Box sx={pageShellSx}>
      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'success')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <PaymentIcon sx={{ fontSize: 120, color: theme.palette.success.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.35)}` }}>
              <PaymentIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gateways de Pagamento</Typography>
              <Typography variant="caption" color="text.secondary">Gerencie gateways para receber pagamentos de clientes</Typography>
            </Box>
          </Stack>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpenForm()} fullWidth={isMobile}>Novo Gateway</Button>
        </Stack>
      </Paper>

      {state.gateways.length > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Total" valor={state.gateways.length} icone={<PaymentIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Ativos" valor={totalAtivos} icone={<CheckIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Sandbox" valor={totalSandbox} icone={<TestTubeIcon sx={{ color: theme.palette.warning.main }} />} cor={theme.palette.warning.main} bgCor={alpha(theme.palette.warning.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Produção" valor={totalProducao} icone={<Payment sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={filtersPaperSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25}>
          <FilterListIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
        </Stack>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" placeholder="Buscar gateways..." value={state.searchTerm} onChange={handleSearch} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={state.selectedType} label="Tipo" onChange={(e) => handleTypeFilter(e.target.value as GatewayType | '')}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="asaas">Asaas</MenuItem>
                <MenuItem value="iugu">Iugu</MenuItem>
                <MenuItem value="efi">EFI/Gerencianet</MenuItem>
                <MenuItem value="sicoob">Sicoob</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadGateways}>Atualizar</Button>
          </Grid>
        </Grid>
      </Paper>

      {state.loading && <LinearProgress sx={{ mb: 1 }} />}
      {state.error && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>{state.error}</Alert>}

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de Gateways</Typography>
          <Chip size="small" label={`${filteredGateways.length} gateway${filteredGateways.length !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        </Box>
        {filteredGateways.length === 0 && !state.loading ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <PaymentIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>Nenhum gateway encontrado</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={rankingTableHeadRowSx(theme)}>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Ambiente</TableCell>
                <TableCell>Emitir NFCom</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Conectividade</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGateways.map((gateway) => (
                <TableRow key={gateway.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src={`/logo-bancos/icones/${gatewayService.getGatewayLogoIcon(gateway.tipo)}`} style={{ width: 20, height: 12 }} />
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                        {gateway.nome}
                      </Typography>
                      {gateway.is_default && (
                        <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} titleAccess="Gateway padrão" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={gatewayService.getGatewayDisplayName(gateway.tipo)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={gateway.ambiente === 'sandbox' ? 'Sandbox' : 'Produção'}
                      color={gateway.ambiente === 'sandbox' ? 'warning' : 'primary'}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={gateway.emitir_nfcom_auto ? 'Sim' : 'Não'}
                      color={gateway.emitir_nfcom_auto ? 'success' : 'default'}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={gateway.ativo ? 'Ativo' : 'Inativo'}
                      color={gateway.ativo ? 'success' : 'default'}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={gateway.status_conectividade === 'conectado' ? 'OK' : 'Falha'}
                      color={gateway.status_conectividade === 'conectado' ? 'success' : 'error'}
                      size="small"
                      icon={gateway.status_conectividade === 'conectado' ? <CheckIcon /> : <CloseIcon />}
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {new Date(gateway.createdAt).toLocaleDateString('pt-BR')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={(e) => handleOpenMenu(e, gateway)}
                      size="small"
                      sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>

      {/* Menu de ações */}
      <Menu
        anchorEl={menu.anchorEl}
        open={Boolean(menu.anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          handleOpenForm(menu.gateway!);
          handleCloseMenu();
        }}>
          <EditIcon sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => {
          handleTestConnection(menu.gateway!);
          handleCloseMenu();
        }}>
          <TestTubeIcon sx={{ mr: 1 }} />
          Testar Conectividade
        </MenuItem>
        <MenuItem onClick={() => {
          handleToggleStatus(menu.gateway!);
          handleCloseMenu();
        }}>
          {menu.gateway?.ativo ? (
            <>
              <CloseIcon sx={{ mr: 1 }} />
              Desativar
            </>
          ) : (
            <>
              <CheckIcon sx={{ mr: 1 }} />
              Ativar
            </>
          )}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          setDialogs(prev => ({
            ...prev,
            confirmDelete: true,
            selectedGateway: menu.gateway
          }));
          handleCloseMenu();
        }} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Excluir
        </MenuItem>
      </Menu>

      {/* Formulário do Gateway */}
      <GatewayForm
        open={dialogs.gatewayForm}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
        gatewayId={dialogs.selectedGateway?.id}
        loading={formLoading}
      />

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={dialogs.confirmDelete}
        onClose={() => setDialogs(prev => ({ ...prev, confirmDelete: false, selectedGateway: null }))}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o gateway "{dialogs.selectedGateway?.nome}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta ação não pode ser desfeita. Todas as faturas associadas a este gateway
            ficarão sem referência.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogs(prev => ({ ...prev, confirmDelete: false, selectedGateway: null }))}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteGateway} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* FAB para mobile */}
      {isMobile && (
        <>
          <Fab
            color="primary"
            aria-label="add"
            sx={{
              position: 'fixed',
              bottom: 86, // Footer height (70px) + spacing (16px) = 86px
              right: 16,
              zIndex: theme.zIndex.appBar - 1, // Abaixo do FAB do dashboard mas acima do footer
            }}
            onClick={() => handleOpenForm()}
          >
            <AddIcon />
          </Fab>

          <Fab
            color="secondary"
            aria-label="refresh"
            sx={{
              position: 'fixed',
              bottom: 86, // Footer height (70px) + spacing (16px) = 86px
              right: 80, // 16px + 56px (FAB width) + 8px spacing = 80px
              zIndex: theme.zIndex.appBar - 1, // Abaixo do FAB do dashboard mas acima do footer
            }}
            onClick={loadGateways}
          >
            <RefreshIcon />
          </Fab>
        </>
      )}
    </Box>
  );
};

export default GatewaysPage;